import React, { useState, useEffect, useRef } from 'react';
import {
  SearchOutlined,
  LikeOutlined,
  LikeFilled,
  MessageOutlined,
  EyeOutlined,
  BookOutlined,
  MoreOutlined,
  PlusOutlined,
  FilePdfOutlined,
  ClockCircleOutlined,
  SendOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  LinkOutlined,
  PlayCircleOutlined,
  CloseCircleFilled,
  InfoCircleOutlined,
  ArrowLeftOutlined,
  LoadingOutlined,
  EyeFilled,
  EditOutlined,
  DownloadOutlined,
  DeleteFilled
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Card,
  DatePicker,
  Input,
  Radio,
  Tabs,
  Tag,
  Select,
  Segmented,
  Dropdown,
  Popconfirm,
  Drawer,
  Empty,
  message,
  Pagination,
  Spin,
  Image,
  List
} from 'antd';
import type { MenuProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { getAllAccount } from 'services/actions/commonServiceAction';
import {
  saveResource,
  resourceDetailsPagenation,
  deleteResource,
  downloadResource,
  resourceupdate,
  resourceDetails,
  resourceLike,
  resourceComment
} from 'services/actions/resourceActoin';
import { resetSaveResourceState } from 'services/slices/resourceSlice';

const { TextArea } = Input;

const mapResourceType = (type: string = ''): 'File' | 'Video' | 'Others' => {
  const lower = (type || '').toLowerCase().trim();
  if (
    lower === 'file' ||
    lower === 'document' ||
    lower === 'doc' ||
    lower === 'pdf' ||
    lower === 'image'
  ) {
    return 'File';
  }
  if (lower === 'link' || lower === 'video') {
    return 'Video';
  }
  return 'Others';
};

const formatFileSize = (bytes: number) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (isoString?: string) => {
  if (!isoString) return '---';
  return dayjs(isoString).format('DD/MM/YYYY');
};

const getTimeAgo = (dateStr?: string) => {
  if (!dateStr) return '';
  const diffDays = dayjs().diff(dayjs(dateStr), 'day');
  if (diffDays === 0) {
    const diffHours = dayjs().diff(dayjs(dateStr), 'hour');
    return diffHours === 0 ? 'Just now' : `${diffHours} hours ago`;
  }
  return `${diffDays} days ago`;
};

const imageFallback =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='%2394a3b8' viewBox='0 0 16 16'><path d='M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z'/><path d='M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z'/></svg>";

const isImageFile = (mimeType?: string, url?: string, fileName?: string) => {
  if (mimeType && mimeType.toLowerCase().startsWith('image/')) return true;
  const target = (url || fileName || '').split('?')[0].split('#')[0];
  return /\.(jpg|jpeg|png|webp|gif|svg|bmp|ico)$/i.test(target);
};

interface CommentItem {
  id?: number;
  user_name?: string;
  comment: string;
  created_on?: string;
}

interface PostItem {
  id: number;
  guid: string;
  author: string;
  avatarText: string;
  avatarColor: string;
  timeAgo: string;
  groupName: string;
  title: string;
  description: string;
  resource_type: 'File' | 'Video' | 'Others';
  mediaUrl: string;
  fileName: string;
  fileSize: string;
  is_public: boolean;
  mime_type?: string;
  is_expired?: boolean;
  expired_on?: string | null;
  expiryDays?: number;
  expiryDate?: string;
  like_count: number;
  is_liked: boolean;
  comment_count: number;
  comments: CommentItem[];
  rawItem: any;
}

const Resources: React.FC = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const userRole = sessionStorage.getItem('role');
  const storedAccountId = sessionStorage.getItem('accountid');
  const hasUserInfo = storedAccountId ? Number(storedAccountId) : 0;

  const [currentScreen, setCurrentScreen] = useState<'feed' | 'create'>('feed');
  const [activeTab, setActiveTab] = useState('feed');
  const [searchTerm, setSearchTerm] = useState('');

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [downloadingGuid, setDownloadingGuid] = useState<string | null>(null);

  const [posts, setPosts] = useState<PostItem[]>([]);
  const [likingId, setLikingId] = useState<number | null>(null);

  // Expanded Post IDs for Inline Comments
  const [expandedCommentPostIds, setExpandedCommentPostIds] = useState<number[]>([]);
  const [commentInputs, setCommentInputs] = useState<{ [postId: number]: string }>({});
  const [submittingCommentPostId, setSubmittingCommentPostId] = useState<number | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<any>(null);
  const [editingItem, setEditingItem] = useState<any>(null);

  const [resourceType, setResourceType] = useState<'File' | 'Video' | 'Others'>('File');
  const [postTitle, setPostTitle] = useState('');
  const [postContent, setPostContent] = useState('');
  const [accessType, setAccessType] = useState<'public' | 'private'>('public');
  const [expiryOption, setExpiryOption] = useState<'none' | '1' | '3' | '5' | '7' | 'custom'>('none');
  const [customExpiryDate, setCustomExpiryDate] = useState<Dayjs | null>(null);
  const [resourceUrl, setResourceUrl] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [selectedAccountIds, setSelectedAccountIds] = useState<(string | number)[]>([0]);

  const { allAccountInfo } = useSelector((state: any) => state.commonData || {});
  const {
    saveResourceLoading,
    saveResourceSuccess,
    saveResourceError,
    resourceReUploadLoading,
    resourceReUploadSuccess,
    resourceReUploadError,
    resourceDetailsPagenationLoading,
    resourceDetailsPagenationInfo
  } = useSelector((state: any) => state.resource || {});

  const fetchResources = (search = '', page = 1, size = 10) => {
    dispatch(
      resourceDetailsPagenation({
        guid: '',
        accountid: hasUserInfo || 0,
        DataFinder: {
          searchdata: search,
          sortbycolumn: 'id',
          sortby: 'desc',
          currentpage: page,
          pagesize: size,
          resource_type: ''
        }
      }) as any
    );
  };

  useEffect(() => {
    dispatch(getAllAccount() as any);
  }, [dispatch]);

  useEffect(() => {
    fetchResources(searchTerm, pageIndex, pageSize);
  }, [pageIndex, pageSize]);

  useEffect(() => {
    if (userRole === 'staff' && activeTab === 'shared') {
      setActiveTab('feed');
    }
  }, [userRole, activeTab]);

  useEffect(() => {
    if (saveResourceSuccess || resourceReUploadSuccess) {
      message.success(
        editingItem ? 'Resource updated successfully!' : 'Resource posted successfully!'
      );
      dispatch(resetSaveResourceState() as any);
      resetForm();
      setCurrentScreen('feed');
      fetchResources(searchTerm, pageIndex, pageSize);
    }
  }, [saveResourceSuccess, resourceReUploadSuccess]);

  useEffect(() => {
    const err = saveResourceError || resourceReUploadError;
    if (err) {
      const errorMsg =
        typeof err === 'string'
          ? err
          : err?.data?.message || err?.message || 'Operation failed. Please try again.';
      message.error(errorMsg);
      dispatch(resetSaveResourceState() as any);
    }
  }, [saveResourceError, resourceReUploadError]);

  const rawApiList: any[] = resourceDetailsPagenationInfo?.ResourceDetail || [];
  const totalRecords: number = resourceDetailsPagenationInfo?.DataFinder?.totalrecords || 0;

  useEffect(() => {
    const formatted: PostItem[] = rawApiList.map((item: any, idx: number) => {
      const mappedType = mapResourceType(item.resource_type);
      const mediaPath = item.file_path || item.resource_url || '';

      let expiryDays: number | undefined = undefined;
      let expiryDateStr: string | undefined = undefined;
      if (item.expired_on) {
        expiryDateStr = formatDate(item.expired_on);
        const diff = dayjs(item.expired_on).diff(dayjs(), 'day');
        expiryDays = diff >= 0 ? diff : 0;
      }

      const avatarLetters = (item.title || 'Res').substring(0, 2).toUpperCase();
      const colors = ['#1677ff', '#13c2c2', '#7c3aed', '#fa8c16', '#52c41a', '#13227a'];
      const avatarColor = colors[idx % colors.length];

      // Extract comments if present in rawItem response
      const commentList: CommentItem[] = Array.isArray(item.comments)
        ? item.comments
        : Array.isArray(item.comment_details)
        ? item.comment_details
        : [];

      return {
        id: item.id,
        guid: item.guid,
        author: item.createdby ? `User #${item.createdby}` : 'Administrator',
        avatarText: avatarLetters,
        avatarColor: avatarColor,
        timeAgo: getTimeAgo(item.created_on || item.modified_on),
        groupName:
          item.account_name && item.account_name.length > 0
            ? item.account_name.join(', ')
            : 'All Accounts',
        title: item.title || item.file_name || 'Untitled Resource',
        description: item.description || '',
        resource_type: mappedType,
        mediaUrl: mediaPath,
        fileName: item.original_file_name || item.file_name || 'Document',
        fileSize: formatFileSize(item.file_size),
        is_public: item.is_public !== false,
        mime_type: item.mime_type,
        is_expired: item.is_expired,
        expired_on: item.expired_on,
        expiryDays: expiryDays,
        expiryDate: expiryDateStr,
        like_count: item.like_count ?? 0,
        is_liked: item.is_liked ?? false,
        comment_count: item.comment_count ?? commentList.length ?? 0,
        comments: commentList,
        rawItem: item
      };
    });

    setPosts(formatted);
  }, [resourceDetailsPagenationInfo]);

  const displayedPosts = posts.filter((post) => {
    if (userRole !== 'staff' && activeTab === 'shared') {
      return post.is_public === false;
    }
    return true;
  });

  const expiringPosts = posts.filter(
    (item) => item.expired_on && !item.is_expired
  );

  const resetForm = () => {
    setEditingItem(null);
    setPostTitle('');
    setPostContent('');
    setSelectedFile(null);
    setPreviewUrl('');
    setResourceUrl('');
    setExpiryOption('none');
    setCustomExpiryDate(null);
    setAccessType('public');
    setSelectedAccountIds([0]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const accOptions: any = [
    { label: 'All', value: 0, key: 0 },
    ...(allAccountInfo?.data?.map((acc: any) => ({
      label: acc?.acct_name,
      value: acc.id,
      key: acc.id
    })) || [])
  ];

  const handleAccountChange = (selectedValues: (string | number)[]) => {
    if (selectedValues.includes(0)) {
      if (selectedValues[selectedValues.length - 1] === 0) {
        setSelectedAccountIds([0]);
      } else {
        setSelectedAccountIds(selectedValues.filter((val) => val !== 0));
      }
    } else {
      setSelectedAccountIds(selectedValues);
    }
  };

  const clearAllAccounts = () => setSelectedAccountIds([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const isLt50MB = file.size / 1024 / 1024 < 50;
      if (!isLt50MB) {
        message.error('File size must be smaller than 50MB!');
        return;
      }
      setSelectedFile(file);
      if (file.type.startsWith('image/')) {
        setPreviewUrl(URL.createObjectURL(file));
      } else {
        setPreviewUrl('');
      }
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getExpiryPreviewText = () => {
    if (expiryOption === 'none') return null;
    if (expiryOption === 'custom') {
      return customExpiryDate
        ? `Expires on ${customExpiryDate.format('DD/MM/YYYY')}`
        : 'Expires on custom date';
    }
    const days = Number(expiryOption);
    const calculatedDate = dayjs().add(days, 'day').format('DD/MM/YYYY');
    return `Expires in ${days} day${days > 1 ? 's' : ''} (${calculatedDate})`;
  };

  const handleViewPost = (post: PostItem) => {
    setDetailItem(post);
    setDrawerOpen(true);
    if (post.guid) {
      const cleanGuid = post.guid.replace(/-/g, '');
      dispatch(resourceDetails({ guid: cleanGuid }) as any);
    }
  };

  const handleEditPost = (post: PostItem) => {
    const raw = post.rawItem;
    setEditingItem(post);
    setPostTitle(raw.title || post.title || '');
    setPostContent(raw.description || post.description || '');
    setResourceType(post.resource_type);
    setAccessType(raw.is_public !== false ? 'public' : 'private');

    let accountIds: number[] = [0];
    if (raw.accountid !== undefined && raw.accountid !== null && raw.accountid !== '') {
      if (typeof raw.accountid === 'string') {
        accountIds = raw.accountid
          .split(',')
          .map((id: string) => Number(id.trim()))
          .filter((id: number) => !isNaN(id));
      } else if (Array.isArray(raw.accountid)) {
        accountIds = raw.accountid.map((id: any) => Number(id));
      } else {
        accountIds = [Number(raw.accountid)];
      }
    }
    setSelectedAccountIds(accountIds.length > 0 ? accountIds : [0]);

    const existingFilePath = raw.file_path || post.mediaUrl || '';
    setResourceUrl(raw.resource_url || existingFilePath);
    if (isImageFile(post.mime_type, existingFilePath, raw.file_name)) {
      setPreviewUrl(existingFilePath);
    }
    setCurrentScreen('create');
  };

  const handleDownloadOrOpen = async (post: PostItem) => {
    if (post.resource_type === 'Video' || post.resource_type === 'Others') {
      if (post.mediaUrl) {
        window.open(post.mediaUrl, '_blank');
      } else {
        message.warning('Link not available');
      }
      return;
    }

    if (!post.guid) {
      message.error('GUID not found for downloading this file');
      return;
    }

    const cleanGuid = post.guid.replace(/-/g, '');
    setDownloadingGuid(post.guid);

    try {
      const res: any = await dispatch(downloadResource(cleanGuid) as any);
      const payloadData =
        res?.payload?.data?.data ||
        res?.payload?.data ||
        res?.payload ||
        res?.data ||
        {};

      const downloadUrl = payloadData?.resource_filepath || post.mediaUrl;
      const downloadName = payloadData?.file_name || post.fileName || 'downloaded-resource';

      if (!downloadUrl) {
        message.error('Download link not found');
        return;
      }

      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Network error');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const downloadAnchor = document.createElement('a');
      downloadAnchor.href = blobUrl;
      downloadAnchor.download = downloadName;
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(post.mediaUrl, '_blank');
    } finally {
      setDownloadingGuid(null);
    }
  };

  const handleDeletePost = async (guid: string) => {
    if (!guid) return;
    const cleanGuid = guid.replace(/-/g, '');
    try {
      const res: any = await dispatch(deleteResource({ guid: cleanGuid }) as any);
      if (res?.error || res?.type?.endsWith('/rejected')) {
        message.error(res?.payload?.message || 'Failed to delete resource');
      } else {
        message.success('Resource post deleted successfully');
        fetchResources(searchTerm, pageIndex, pageSize);
      }
    } catch (err: any) {
      message.error(err?.message || 'Failed to delete');
    }
  };

  // Like Click Handler
  const handleLikeClick = async (postId: number) => {
    setLikingId(postId);
    try {
      const res: any = await dispatch(resourceLike({ resource_id: postId }) as any);
      const payload = res?.payload?.data || res?.payload || res?.data;

      if (payload && (payload.status === 'success' || payload.data)) {
        const responseData = payload.data || payload;
        setPosts((prev) =>
          prev.map((item) => {
            if (item.id === postId) {
              const updatedLiked = responseData.is_liked !== undefined ? responseData.is_liked : !item.is_liked;
              const updatedCount = responseData.like_count !== undefined ? responseData.like_count : updatedLiked ? item.like_count + 1 : Math.max(0, item.like_count - 1);

              return {
                ...item,
                is_liked: updatedLiked,
                like_count: updatedCount
              };
            }
            return item;
          })
        );
      } else {
        message.error(payload?.message || 'Failed to update like');
      }
    } catch (err: any) {
      message.error(err?.message || 'Failed to update like');
    } finally {
      setLikingId(null);
    }
  };

  // Toggle inline comment section
  const handleToggleComments = (postId: number) => {
    setExpandedCommentPostIds((prev) =>
      prev.includes(postId) ? prev.filter((id) => id !== postId) : [...prev, postId]
    );
  };

  // Submit comment inline
  const handleInlineCommentSubmit = async (postId: number) => {
    const text = (commentInputs[postId] || '').trim();
    if (!text) {
      message.warning('Please enter a comment');
      return;
    }

    setSubmittingCommentPostId(postId);
    try {
      const payload = {
        resource_id: postId,
        comment: text
      };

      const res: any = await dispatch(resourceComment(payload) as any);
      const resData = res?.payload || res?.data;

      if (resData?.status === 'success' || resData?.comment_count !== undefined) {
        message.success(resData?.message || 'Comment added successfully');

        setPosts((prev) =>
          prev.map((p) => {
            if (p.id === postId) {
              const updatedCount =
                resData?.comment_count !== undefined
                  ? resData.comment_count
                  : p.comment_count + 1;

              const newComment: CommentItem = {
                id: Date.now(),
                user_name: 'You',
                comment: text,
                created_on: dayjs().toISOString()
              };

              return {
                ...p,
                comment_count: updatedCount,
                comments: [...(p.comments || []), newComment]
              };
            }
            return p;
          })
        );

        setCommentInputs((prev) => ({ ...prev, [postId]: '' }));
      } else {
        message.error(resData?.message || 'Failed to add comment');
      }
    } catch (err: any) {
      message.error(err?.message || 'Failed to post comment');
    } finally {
      setSubmittingCommentPostId(null);
    }
  };

  const getActionMenuItems = (post: PostItem): MenuProps['items'] => {
    const isStaff = userRole === 'staff';

    const items: MenuProps['items'] = [
      {
        key: 'view',
        icon: <EyeFilled className="text-primary" />,
        label: 'View Details',
        onClick: () => handleViewPost(post)
      },
      {
        key: 'download',
        icon:
          post.resource_type === 'File' ? (
            <DownloadOutlined className="text-success" />
          ) : (
            <LinkOutlined className="text-primary" />
          ),
        label: post.resource_type === 'File' ? 'Download File' : 'Open Link',
        onClick: () => handleDownloadOrOpen(post)
      }
    ];

    if (!isStaff) {
      items.push(
        {
          key: 'edit',
          icon: <EditOutlined className="text-warning" />,
          label: 'Edit Post',
          onClick: () => handleEditPost(post)
        },
        {
          type: 'divider'
        },
        {
          key: 'delete',
          danger: true,
          label: (
            <Popconfirm
              title="Delete Post"
              description="Are you sure you want to delete this resource post?"
              onConfirm={() => handleDeletePost(post.guid)}
              okText="Yes"
              cancelText="No"
            >
              <div className="d-flex align-items-center gap-2 text-danger">
                <DeleteFilled />
                <span>Delete</span>
              </div>
            </Popconfirm>
          )
        }
      );
    }

    return items;
  };

  const handleSharePost = () => {
    if (!postTitle.trim()) {
      message.error('Please provide a post title');
      return;
    }

    const isLinkBased = resourceType === 'Video' || resourceType === 'Others';

    let formattedResourceType = 'document';
    if (resourceType === 'Others') {
      formattedResourceType = 'others';
    } else if (resourceType === 'Video') {
      formattedResourceType = 'link';
    }

    if (isLinkBased && !resourceUrl.trim()) {
      message.error(`Please enter the ${resourceType === 'Video' ? 'video' : 'resource'} URL`);
      return;
    }

    if (!isLinkBased && !selectedFile && !editingItem) {
      message.error('Please select a valid file to attach.');
      return;
    }

    if (expiryOption === 'custom' && !customExpiryDate) {
      message.error('Please select an end date for custom expiry.');
      return;
    }

    const isPublic = accessType !== 'private';
    let formattedAccountIds: number[] = [0];

    if (isPublic) {
      const selectedAccs = Array.isArray(selectedAccountIds)
        ? selectedAccountIds
        : [selectedAccountIds];

      if (!selectedAccs.includes(0) && selectedAccs.length > 0) {
        formattedAccountIds = selectedAccs
          .filter((id: any) => id !== undefined && id !== null && id !== '')
          .map((id: any) => Number(id));
      }
    }

    let expiredOnValue: string | number = 0;
    if (expiryOption === 'none') {
      expiredOnValue = 0;
    } else if (expiryOption === 'custom') {
      expiredOnValue = customExpiryDate ? customExpiryDate.format('YYYY-MM-DD') : 0;
    } else {
      const days = Number(expiryOption);
      expiredOnValue = dayjs().add(days, 'day').format('YYYY-MM-DD');
    }

    const formData = new FormData();

    if (editingItem) {
      const cleanGuid = (editingItem?.guid || '').replace(/-/g, '');
      const updateInputJson = {
        guid: cleanGuid,
        title: postTitle,
        description: postContent || '',
        is_public: isPublic ? 'True' : 'False',
        resource_url: isLinkBased ? resourceUrl : null,
        accountid: formattedAccountIds
      };

      if (!isLinkBased && selectedFile) {
        formData.append('file', selectedFile, selectedFile.name || 'uploaded_file');
      }

      formData.append('inputjson', JSON.stringify(updateInputJson));
      dispatch(resourceupdate(formData) as any);
      return;
    }

    const inputjson = {
      title: postTitle,
      description: postContent || '',
      resource_type: formattedResourceType,
      resource_url: isLinkBased ? resourceUrl : '',
      accountid: formattedAccountIds,
      is_public: isPublic,
      is_expired: false,
      expired_on: expiredOnValue
    };

    if (!isLinkBased && selectedFile) {
      formData.append('file', selectedFile, selectedFile.name || 'uploaded_file');
    }

    formData.append('inputjson', JSON.stringify(inputjson));
    dispatch(saveResource(formData) as any);
  };

  const handleSearchTrigger = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setPageIndex(1);
      fetchResources(searchTerm, 1, pageSize);
    }
  };

  return (
    <div className="bg-light min-vh-100 p-4">
      {currentScreen === 'feed' && (
        <div className="d-flex justify-content-center gap-4">
          <div className="flex-grow-1" style={{ maxWidth: '820px' }}>
            <div className="bg-white px-3 pt-2 rounded-3 shadow-sm mb-3">
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                className="w-100 m-0"
                items={[
                  {
                    key: 'feed',
                    label: `Feed (${totalRecords || displayedPosts.length})`
                  },
                  ...(userRole !== 'staff'
                    ? [
                        {
                          key: 'shared',
                          label: `Shared with Me (${displayedPosts.filter((p) => !p.is_public).length})`
                        }
                      ]
                    : [])
                ]}
              />
            </div>

            <Spin spinning={resourceDetailsPagenationLoading}>
              <div className="d-flex flex-column gap-3">
                {displayedPosts.length === 0 ? (
                  <Card className="shadow-sm border-0 rounded-3 text-center py-5">
                    <Empty
                      description={
                        activeTab === 'shared'
                          ? 'No private resources shared with you.'
                          : 'No resources available.'
                      }
                    />
                  </Card>
                ) : (
                  displayedPosts.map((post) => {
                    const isImg = isImageFile(post.mime_type, post.mediaUrl, post.fileName);
                    const safeImageUrl = post.mediaUrl ? encodeURI(post.mediaUrl) : '';
                    const isCommentsExpanded = expandedCommentPostIds.includes(post.id);

                    return (
                      <Card key={post.id} className="shadow-sm border-0 rounded-3">
                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-start w-100 mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <Avatar style={{ backgroundColor: post.avatarColor }}>
                              {post.avatarText}
                            </Avatar>
                            <div>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="fw-bold text-dark">{post.author}</span>
                                {userRole !== 'staff' && (
                                  <Tag
                                    color={post.is_public ? 'blue' : 'warning'}
                                    className="m-0 rounded-pill px-2"
                                    style={{ fontSize: 11 }}
                                  >
                                    {post.is_public ? 'Public' : 'Private'}
                                  </Tag>
                                )}
                              </div>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                {post.timeAgo || 'Recently'} • {post.groupName}
                              </div>
                            </div>
                          </div>

                          <div className="ms-auto">
                            <Dropdown
                              menu={{ items: getActionMenuItems(post) }}
                              trigger={['click']}
                              placement="bottomRight"
                            >
                              <Button type="text" shape="circle" icon={<MoreOutlined />} />
                            </Dropdown>
                          </div>
                        </div>

                        <h6 className="fw-bold text-dark mt-2 mb-1">{post.title}</h6>
                        <p className="text-secondary small mb-3">{post.description || '-'}</p>

                        {post.resource_type === 'File' && isImg && safeImageUrl && (
                          <div
                            className="mb-3 rounded-3 overflow-hidden border d-flex align-items-center justify-content-center bg-light"
                            style={{ minHeight: 180, maxHeight: 320 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Image
                              src={safeImageUrl}
                              alt={post.title}
                              fallback={imageFallback}
                              placeholder={
                                <div
                                  className="d-flex align-items-center justify-content-center w-100"
                                  style={{ height: 180, backgroundColor: '#f8fafc' }}
                                >
                                  <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                                </div>
                              }
                              style={{
                                maxHeight: 300,
                                width: '100%',
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                        )}

                        {post.resource_type === 'File' && (!isImg || !safeImageUrl) && (
                          <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 mb-3 border">
                            <div className="d-flex align-items-center gap-3">
                              <FilePdfOutlined style={{ fontSize: 32, color: '#f5222d' }} />
                              <div>
                                <div className="fw-semibold text-dark small">{post.fileName}</div>
                                <div className="text-muted" style={{ fontSize: 11 }}>
                                  {post.fileSize}
                                </div>
                              </div>
                            </div>
                            <Button
                              size="small"
                              type="primary"
                              loading={downloadingGuid === post.guid}
                              onClick={() => handleDownloadOrOpen(post)}
                            >
                              Download
                            </Button>
                          </div>
                        )}

                        {post.resource_type === 'Video' && (
                          <div
                            className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 mb-3 border pointer"
                            onClick={() => handleDownloadOrOpen(post)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <PlayCircleOutlined style={{ fontSize: 32, color: '#7c3aed' }} />
                              <div>
                                <div className="fw-semibold text-dark small">Video Resource</div>
                                <div
                                  className="text-muted small text-truncate"
                                  style={{ maxWidth: 300 }}
                                >
                                  {post.mediaUrl || 'Streaming Link'}
                                </div>
                              </div>
                            </div>
                            <Button size="small" type="link">
                              Watch Video
                            </Button>
                          </div>
                        )}

                        {post.resource_type === 'Others' && (
                          <div
                            className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 mb-3 border pointer"
                            onClick={() => handleDownloadOrOpen(post)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <LinkOutlined style={{ fontSize: 32, color: '#0284c7' }} />
                              <div>
                                <div className="fw-semibold text-dark small">External Resource</div>
                                <div
                                  className="text-muted small text-truncate"
                                  style={{ maxWidth: 300 }}
                                >
                                  {post.mediaUrl || 'Web Link'}
                                </div>
                              </div>
                            </div>
                            <Button size="small" type="link">
                              Open Link
                            </Button>
                          </div>
                        )}

                        <div className="d-flex align-items-center gap-2 mb-3">
                          <Tag className="rounded-pill text-primary bg-primary-subtle border-0">
                            {post.resource_type}
                          </Tag>
                          {post.expiryDays !== undefined && post.expiryDays > 0 && (
                            <span className="text-warning small d-flex align-items-center gap-1">
                              <ClockCircleOutlined /> Expires in {post.expiryDays} days (
                              {post.expiryDate})
                            </span>
                          )}
                          {post.is_expired && (
                            <span className="text-danger small d-flex align-items-center gap-1">
                              <ClockCircleOutlined /> Expired ({post.expiryDate})
                            </span>
                          )}
                        </div>

                        {/* Interactive Footer (Like, Comment, Views) */}
                        <div className="d-flex justify-content-between align-items-center pt-2 border-top text-muted small">
                          <div className="d-flex align-items-center gap-4">
                            {/* Like Trigger */}
                            <span
                              className={`d-flex align-items-center gap-1 ${
                                post.is_liked ? 'text-primary fw-semibold' : ''
                              }`}
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                              onClick={() => handleLikeClick(post.id)}
                            >
                              {likingId === post.id ? (
                                <LoadingOutlined spin />
                              ) : post.is_liked ? (
                                <LikeFilled style={{ color: '#1677ff', fontSize: 16 }} />
                              ) : (
                                <LikeOutlined style={{ fontSize: 16 }} />
                              )}
                              <span>{post.like_count}</span>
                            </span>

                            {/* Comment Trigger (Expands Section Below) */}
                            <span
                              className={`d-flex align-items-center gap-1 ${
                                isCommentsExpanded ? 'text-primary fw-semibold' : ''
                              }`}
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                              onClick={() => handleToggleComments(post.id)}
                            >
                              <MessageOutlined
                                style={{
                                  fontSize: 16,
                                  color: isCommentsExpanded ? '#1677ff' : undefined
                                }}
                              />
                              <span>{post.comment_count}</span>
                            </span>

                            {/* Views */}
                            <span className="d-flex align-items-center gap-1">
                              <EyeOutlined style={{ fontSize: 16 }} /> 0
                            </span>
                          </div>

                          <div className="d-flex align-items-center gap-3">
                            <BookOutlined style={{ cursor: 'pointer', fontSize: 16 }} />
                          </div>
                        </div>

                        {/* Extended Inline Comments Section */}
                        {isCommentsExpanded && (
                          <div className="mt-3 pt-3 border-top bg-light p-3 rounded-3">
                            <div className="fw-semibold text-dark small mb-2">
                              Comments ({post.comments?.length || post.comment_count || 0})
                            </div>

                            {/* Comments List */}
                            {post.comments && post.comments.length > 0 ? (
                              <List
                                size="small"
                                dataSource={post.comments}
                                className="mb-3 bg-white rounded-2 border p-2"
                                style={{ maxHeight: '200px', overflowY: 'auto' }}
                                renderItem={(c: CommentItem, idx: number) => (
                                  <List.Item
                                    key={c.id || idx}
                                    className="px-2 py-1 border-0 border-bottom"
                                  >
                                    <div className="d-flex flex-column w-100">
                                      <div className="d-flex justify-content-between align-items-center">
                                        <span className="fw-bold small text-primary">
                                          {c.user_name || 'User'}
                                        </span>
                                        <span className="text-muted" style={{ fontSize: 10 }}>
                                          {c.created_on
                                            ? getTimeAgo(c.created_on)
                                            : 'Just now'}
                                        </span>
                                      </div>
                                      <span className="text-secondary small mt-1">
                                        {c.comment}
                                      </span>
                                    </div>
                                  </List.Item>
                                )}
                              />
                            ) : (
                              <div className="text-muted small text-center my-2 p-2 bg-white rounded-2 border">
                                No comments yet. Be the first to comment!
                              </div>
                            )}

                            {/* Comment Input Box */}
                            <div className="d-flex flex-column gap-2">
                              <TextArea
                                rows={2}
                                placeholder="Write a comment..."
                                className="rounded-2"
                                value={commentInputs[post.id] || ''}
                                onChange={(e) =>
                                  setCommentInputs((prev) => ({
                                    ...prev,
                                    [post.id]: e.target.value
                                  }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleInlineCommentSubmit(post.id);
                                  }
                                }}
                              />
                              <div className="d-flex justify-content-end">
                                <Button
                                  type="primary"
                                  size="small"
                                  icon={
                                    submittingCommentPostId === post.id ? (
                                      <LoadingOutlined />
                                    ) : (
                                      <SendOutlined />
                                    )
                                  }
                                  loading={submittingCommentPostId === post.id}
                                  onClick={() => handleInlineCommentSubmit(post.id)}
                                >
                                  Comment
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })
                )}

                {totalRecords > 0 && (
                  <div className="d-flex justify-content-end align-items-center mt-3 pt-2">
                    <Pagination
                      current={pageIndex}
                      pageSize={pageSize}
                      total={totalRecords}
                      showSizeChanger={true}
                      pageSizeOptions={['5', '10', '20', '50']}
                      onChange={(page, size) => {
                        setPageIndex(page);
                        setPageSize(size);
                      }}
                    />
                  </div>
                )}
              </div>
            </Spin>
          </div>

          <div
            className="d-none d-lg-flex flex-column gap-3"
            style={{ width: '320px', minWidth: '320px' }}
          >
            <Input
              prefix={<SearchOutlined className="text-muted" />}
              placeholder="Search by title & hit Enter..."
              className="rounded-3 py-2 bg-white shadow-sm border-0"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchTrigger}
              allowClear
            />

            {userRole !== 'staff' && (
              <Card className="border-0 shadow-sm rounded-3">
                <Button
                  type="primary"
                  block
                  icon={<PlusOutlined />}
                  onClick={() => {
                    resetForm();
                    setCurrentScreen('create');
                  }}
                >
                  Create Post
                </Button>
              </Card>
            )}

            <Card
              className="border-0 shadow-sm rounded-3"
              styles={{
                body: {
                  padding: '20px',
                  minHeight: '470px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start'
                }
              }}
            >
              <div>
                <div className="d-flex justify-content-between align-items-center w-100 mb-3">
                  <h6 className="fw-bold m-0 text-dark">
                    Expiring Posts ({expiringPosts.length})
                  </h6>
                </div>

                <div
                  className="d-flex flex-column gap-3 pe-1"
                  style={{ maxHeight: '380px', overflowY: 'auto' }}
                >
                  {expiringPosts.length === 0 ? (
                    <div className="text-muted small text-center my-4">
                      No active expiring posts
                    </div>
                  ) : (
                    expiringPosts.map((item, idx) => {
                      const badgeColor =
                        (item.expiryDays ?? 0) <= 2
                          ? 'text-danger'
                          : (item.expiryDays ?? 0) <= 5
                          ? 'text-warning'
                          : 'text-muted';

                      const isImg = isImageFile(item.mime_type, item.mediaUrl, item.fileName);
                      const safeThumbUrl = item.mediaUrl ? encodeURI(item.mediaUrl) : '';

                      return (
                        <div
                          key={`${item.id}-${idx}`}
                          className="d-flex align-items-center gap-2 py-1 pointer"
                          onClick={() => handleViewPost(item)}
                          style={{ cursor: 'pointer' }}
                        >
                          {safeThumbUrl && isImg ? (
                            <Image
                              src={safeThumbUrl}
                              alt={item.title}
                              preview={false}
                              fallback={imageFallback}
                              className="rounded-2 object-fit-cover"
                              wrapperStyle={{ width: 44, height: 44, flexShrink: 0 }}
                              style={{ width: 44, height: 44, objectFit: 'cover' }}
                            />
                          ) : (
                            <div
                              className="rounded-2 bg-secondary-subtle d-flex align-items-center justify-content-center text-secondary"
                              style={{ width: 44, height: 44, flexShrink: 0 }}
                            >
                              <ClockCircleOutlined />
                            </div>
                          )}

                          <div className="lh-sm flex-grow-1 overflow-hidden">
                            <div className="fw-semibold text-truncate small" title={item.title}>
                              {item.title}
                            </div>
                            <div className="text-muted mb-1" style={{ fontSize: 11 }}>
                              {item.groupName}
                            </div>
                            <div className={`small ${badgeColor}`} style={{ fontSize: 11 }}>
                              <ClockCircleOutlined /> {item.expiryDays} days left ({item.expiryDate})
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {currentScreen === 'create' && (
        <div className="container-fluid px-3" style={{ maxWidth: '1240px' }}>
          <div className="mb-3 d-flex align-items-center gap-2">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => {
                resetForm();
                setCurrentScreen('feed');
              }}
              className="fw-semibold"
            >
              Back to Feed
            </Button>
          </div>

          <div className="row g-4 align-items-stretch">
            {/* Left Column: Form Fields */}
            <div className="col-lg-7 d-flex flex-column">
              <Card className="border-0 shadow-sm rounded-3 p-3 h-100 d-flex flex-column">
                <div className="mb-4">
                  <h5 className="fw-bold text-dark m-0">
                    {editingItem ? 'Edit Resource Post' : 'Create Post'}
                  </h5>
                  <p className="text-muted small m-0">
                    Create a post and share it to multiple accounts/groups with time settings.
                  </p>
                </div>

                <div className="mb-3">
                  <label className="fw-bold small mb-2 d-block">Access Type</label>
                  <Radio.Group
                    value={accessType}
                    onChange={(e) => {
                      setAccessType(e.target.value);
                      if (e.target.value === 'private') {
                        setSelectedAccountIds([0]);
                      }
                    }}
                  >
                    <Radio value="public">Public</Radio>
                    <Radio value="private">Private</Radio>
                  </Radio.Group>
                </div>

                <div className="mb-3">
                  <label className="fw-bold small mb-2 d-block">Type</label>
                  <Segmented
                    block
                    value={resourceType}
                    onChange={(val) => setResourceType(val as 'File' | 'Video' | 'Others')}
                    options={['File', 'Video', 'Others']}
                  />
                </div>

                <div className="border rounded-3 p-3 bg-light mb-3">
                  <Input
                    value={postTitle}
                    onChange={(e) => setPostTitle(e.target.value)}
                    placeholder="Post Title..."
                    className="fw-bold bg-transparent border-0 px-0 mb-2"
                    style={{ fontSize: 15 }}
                  />
                  <TextArea
                    value={postContent}
                    onChange={(e) => setPostContent(e.target.value)}
                    rows={4}
                    className="bg-transparent border-0 px-0"
                    placeholder="Write your post here or provide description..."
                  />
                </div>

                {(resourceType === 'Video' || resourceType === 'Others') && (
                  <div className="mb-3">
                    <label className="fw-bold small mb-2 d-block">
                      {resourceType === 'Video' ? 'Video Streaming Link' : 'Resource / Website Link'}
                    </label>
                    <Input
                      prefix={
                        resourceType === 'Video' ? (
                          <VideoCameraOutlined style={{ color: '#7c3aed' }} />
                        ) : (
                          <LinkOutlined style={{ color: '#0284c7' }} />
                        )
                      }
                      placeholder={
                        resourceType === 'Video'
                          ? 'Paste YouTube, Vimeo, Google Drive video or MP4 link...'
                          : 'Paste Google Drive, Dropbox, Notion, or any Web link...'
                      }
                      value={resourceUrl}
                      onChange={(e) => setResourceUrl(e.target.value)}
                    />
                  </div>
                )}

                {resourceType === 'File' && (
                  <div className="mb-4">
                    <label className="fw-bold small mb-2 d-block">
                      File Attachment {selectedFile ? `(1 file: ${selectedFile.name})` : ''}
                    </label>
                    <div className="d-flex gap-3 align-items-center">
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,image/*"
                      />

                      {(previewUrl || selectedFile) && (
                        <div className="position-relative" style={{ width: 120, height: 90 }}>
                          {previewUrl ? (
                            <img
                              src={previewUrl}
                              alt="preview"
                              className="rounded-3 w-100 h-100 object-fit-cover border"
                            />
                          ) : (
                            <div className="rounded-3 w-100 h-100 border d-flex flex-column align-items-center justify-content-center bg-white p-1">
                              <FileTextOutlined style={{ fontSize: 24, color: '#4338ca' }} />
                              <span
                                className="small text-truncate w-100 text-center"
                                style={{ fontSize: 10 }}
                              >
                                {selectedFile?.name}
                              </span>
                            </div>
                          )}
                          <CloseCircleFilled
                            className="position-absolute text-danger bg-white rounded-circle"
                            style={{ top: -6, right: -6, fontSize: 18, cursor: 'pointer' }}
                            onClick={removeSelectedFile}
                          />
                        </div>
                      )}

                      <div
                        className="border border-dashed rounded-3 d-flex flex-column align-items-center justify-content-center text-muted p-2"
                        style={{ width: 160, height: 90, cursor: 'pointer' }}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <PlusOutlined style={{ fontSize: 18 }} />
                        <div className="text-center small mt-1" style={{ fontSize: 11 }}>
                          {selectedFile ? 'Replace File' : 'Click to Upload'}
                          <br />
                          <span className="text-secondary">(Max 50MB)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mb-2 mt-auto">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="fw-semibold small m-0">Select Accounts / Groups</label>
                    <div className="d-flex align-items-center gap-3">
                      <span className="small text-muted">
                        Selected:{' '}
                        <b>
                          {selectedAccountIds.includes(0)
                            ? 'All Accounts'
                            : `${selectedAccountIds.length} accounts`}
                        </b>
                      </span>
                      <Button
                        type="link"
                        size="small"
                        className="p-0 text-danger"
                        onClick={clearAllAccounts}
                        disabled={accessType === 'private'}
                      >
                        Clear All
                      </Button>
                    </div>
                  </div>

                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Choose an account"
                    showSearch
                    disabled={accessType === 'private'}
                    value={selectedAccountIds}
                    options={accOptions}
                    filterOption={(input: any, option: any) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={handleAccountChange}
                  />
                </div>
              </Card>
            </div>

            <div className="col-lg-5 d-flex flex-column gap-3 h-100">
              <Card
                className="border-0 shadow-sm rounded-3 flex-grow-1 d-flex flex-column"
                styles={{
                  body: {
                    padding: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold m-0 text-dark">Post Preview</h6>
                  <div className="d-flex gap-1">
                    <Tag
                      color={
                        resourceType === 'Video'
                          ? 'purple'
                          : resourceType === 'Others'
                          ? 'cyan'
                          : 'blue'
                      }
                    >
                      {resourceType}
                    </Tag>
                    <Tag color={accessType === 'public' ? 'blue' : 'gold'}>
                      {accessType === 'public' ? 'Public' : 'Private'}
                    </Tag>
                  </div>
                </div>

                <div className="border rounded-3 p-3 bg-white flex-grow-1 d-flex flex-column overflow-auto">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <Avatar style={{ backgroundColor: '#1677ff' }}>AD</Avatar>
                      <div className="lh-sm">
                        <div className="fw-bold small">You</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          Now
                        </div>
                      </div>
                    </div>
                    <MoreOutlined className="text-muted" />
                  </div>

                  <div className="fw-bold small text-dark mb-1">{postTitle || 'Untitled Post'}</div>
                  <p className="text-secondary small mb-3 flex-grow-1" style={{ fontSize: 12 }}>
                    {postContent || 'No description provided.'}
                  </p>

                  {resourceType === 'File' && previewUrl && (
                    <div
                      className="rounded-3 overflow-hidden mb-3 text-center"
                      style={{ maxHeight: 200 }}
                    >
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-100 h-100 object-fit-cover"
                      />
                    </div>
                  )}

                  {resourceType === 'Video' && (
                    <div className="p-3 bg-light rounded-3 text-center mb-2 border">
                      <PlayCircleOutlined style={{ fontSize: 32, color: '#7c3aed' }} />
                      <div className="small text-muted text-truncate mt-1">
                        {resourceUrl || 'Video Link Stream'}
                      </div>
                    </div>
                  )}

                  {resourceType === 'Others' && (
                    <div className="p-3 bg-light rounded-3 text-center mb-2 border">
                      <LinkOutlined style={{ fontSize: 32, color: '#0284c7' }} />
                      <div className="small text-muted text-truncate mt-1">
                        {resourceUrl || 'External Web Link'}
                      </div>
                    </div>
                  )}

                  {getExpiryPreviewText() && (
                    <div className="d-flex align-items-center gap-1 text-warning small mt-auto pt-2 border-top">
                      <ClockCircleOutlined />
                      <span>{getExpiryPreviewText()}</span>
                    </div>
                  )}
                </div>
              </Card>

              <Card className="border-0 shadow-sm rounded-3 mt-auto" styles={{ body: { padding: 16 } }}>
                <div className="mb-3 p-3 border rounded-3 bg-white">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="fw-bold small m-0 d-flex align-items-center gap-1">
                      <ClockCircleOutlined className="text-primary" /> Auto Expire Post
                    </label>
                    <span className="text-muted small" style={{ fontSize: 11 }}>
                      Post will expire after date
                    </span>
                  </div>

                  <Radio.Group
                    value={expiryOption}
                    onChange={(e) => setExpiryOption(e.target.value)}
                    className="d-flex flex-wrap gap-2 mb-2"
                  >
                    <Radio.Button value="none">None</Radio.Button>
                    <Radio.Button value="1">1 Day</Radio.Button>
                    <Radio.Button value="3">3 Days</Radio.Button>
                    <Radio.Button value="5">5 Days</Radio.Button>
                    <Radio.Button value="custom">Custom</Radio.Button>
                  </Radio.Group>

                  {expiryOption === 'custom' && (
                    <div className="mt-2 pt-2 border-top">
                      <label className="fw-semibold small text-muted d-block mb-1">
                        Select End Date:
                      </label>
                      <DatePicker
                        className="w-100"
                        placeholder="Choose expiration date"
                        value={customExpiryDate}
                        onChange={(date) => setCustomExpiryDate(date)}
                        disabledDate={(current) =>
                          current && current < dayjs().endOf('day')
                        }
                      />
                    </div>
                  )}
                </div>

                <div className="bg-light p-2 rounded-2 border d-flex align-items-start gap-2 mb-3">
                  <InfoCircleOutlined className="text-primary mt-1" />
                  <div className="small text-muted" style={{ fontSize: 11 }}>
                    The post will be visible to selected{' '}
                    {selectedAccountIds.includes(0) ? 'all' : selectedAccountIds.length} accounts.
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 pt-1">
                  <Button onClick={() => setCurrentScreen('feed')}>Cancel</Button>
                  <Button
                    type="primary"
                    icon={
                      saveResourceLoading || resourceReUploadLoading ? (
                        <LoadingOutlined />
                      ) : (
                        <SendOutlined />
                      )
                    }
                    loading={saveResourceLoading || resourceReUploadLoading}
                    onClick={handleSharePost}
                  >
                    {editingItem ? 'Update Post' : 'Share Post'}
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Drawer Details */}
      <Drawer
        title={<span style={{ fontWeight: 700 }}>Resource Details</span>}
        placement="right"
        width={440}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
      >
        {detailItem && (
          <div className="d-flex flex-column gap-3">
            <div>
              <h5 className="fw-bold text-dark mb-1">{detailItem.title}</h5>
              <div className="d-flex align-items-center gap-2">
                <Tag color={detailItem.resource_type === 'Video' ? 'purple' : 'blue'}>
                  {detailItem.resource_type}
                </Tag>
                {userRole !== 'staff' && (
                  <Tag color={detailItem.is_public ? 'green' : 'orange'}>
                    {detailItem.is_public ? 'Public' : 'Private'}
                  </Tag>
                )}
              </div>
            </div>

            <div>
              <h6 className="fw-bold text-dark mb-1">Description</h6>
              <p className="text-muted small">
                {detailItem.description || 'No description provided.'}
              </p>
            </div>

            <hr className="my-1" />

            <div>
              <h6 className="fw-bold text-dark mb-2">Properties</h6>
              <div className="d-flex justify-content-between mb-2 small">
                <span className="text-muted">Group / Accounts:</span>
                <span className="fw-semibold text-truncate" style={{ maxWidth: 220 }}>
                  {detailItem.groupName}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-2 small">
                <span className="text-muted">File Size:</span>
                <span className="fw-semibold">{detailItem.fileSize}</span>
              </div>
              <div className="d-flex justify-content-between mb-2 small">
                <span className="text-muted">Expiry Date:</span>
                <span className="fw-semibold text-warning">
                  {detailItem.expiryDate || 'No Expiry'}
                </span>
              </div>
            </div>

            <div className="mt-3">
              <Button
                type="primary"
                block
                icon={
                  detailItem.resource_type === 'File' ? (
                    <DownloadOutlined />
                  ) : (
                    <LinkOutlined />
                  )
                }
                onClick={() => handleDownloadOrOpen(detailItem)}
              >
                {detailItem.resource_type === 'File'
                  ? 'Download Resource'
                  : 'Open Resource Link'}
              </Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default Resources;